import axios from 'axios';
import i18next from 'i18next';
import { localStorageService } from './localStorage.service';
import { toasterMsg, suppressNavigationToasts } from '../Components/Toaster/Toaster';
import history from '../history';
import { installGetDedupe } from './request-dedupe';
import { noteMasterMutation } from '../store/master-cache';
import { reportNetworkFailure, reportNetworkSuccess } from './connectivity.service';

/**
 * Route-change cancellation for in-flight GET requests.
 *
 * When the user navigates to another page, responses of the previous page's
 * pending GETs would be discarded anyway (their components unmount), yet the
 * server keeps burning CPU on them. Cancelling them on pathname change frees
 * server capacity and stops rapid tab-clicking from stacking heavy queries.
 *
 * Safety rules — a request is auto-cancelled ONLY when ALL of these hold:
 * - method is GET (mutations: POST/PUT/PATCH/DELETE are NEVER cancelled)
 * - responseType is not blob/arraybuffer (file downloads keep running)
 * - the caller did not attach its own cancelToken
 * - the caller did not opt out via the `X-Keep-On-Navigate` header
 * Cancelled requests reject with a flagged error so callers' catch AND finally
 * blocks still run (clearing loading flags); the toaster suppresses the brief
 * error-toast cascade those rejections trigger on the page being left.
 */
const inFlightNavCancelSources = new Set();
const NAV_CANCEL_EXEMPT_RESPONSE_TYPES = ['blob', 'arraybuffer'];

const isNavigationCancellable = (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get') return false;
  if (NAV_CANCEL_EXEMPT_RESPONSE_TYPES.includes(config.responseType)) return false;
  if (config.cancelToken) return false;
  if (config.headers?.['X-Keep-On-Navigate']) return false;
  return true;
};

const untrackNavCancelSource = (config) => {
  if (config?.__navCancelSource) {
    inFlightNavCancelSources.delete(config.__navCancelSource);
  }
};

let lastNavPathname = window.location.pathname;
history.listen((update) => {
  const location = update?.location || update;
  if (!location?.pathname || location.pathname === lastNavPathname) return;
  lastNavPathname = location.pathname;
  const pendingSources = Array.from(inFlightNavCancelSources);
  inFlightNavCancelSources.clear();
  if (pendingSources.length > 0) {
    suppressNavigationToasts();
    pendingSources.forEach((source) => source.cancel('route-change'));
  }
});

export const apiClient = installGetDedupe(axios.create({ withCredentials: true }));
const slugName = window.location.hostname?.split('.')?.[0];
export const apiEndpoint = `${process.env.REACT_APP_PROTOCOL}${slugName}${process.env.REACT_APP_API_URL}/api`;

const userInfo = localStorageService.get('loggedInUserInfo');
if (userInfo) {
  apiClient.defaults.headers.common.Authorization = `Bearer ${userInfo.token}`;
}

apiClient.interceptors.request.use((config) => {
  const currentUser = localStorageService.get('loggedInUserInfo');
  if (currentUser?.token) {
    config.headers.Authorization = `Bearer ${currentUser.token}`;
  }
  if (isNavigationCancellable(config)) {
    const source = axios.CancelToken.source();
    config.cancelToken = source.token;
    config.__navCancelSource = source;
    inFlightNavCancelSources.add(source);
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    untrackNavCancelSource(response?.config);
    reportNetworkSuccess();
    noteMasterMutation(response?.config);

    // Skip interceptor logic for blob responses (file downloads)
    if (response.data instanceof Blob) {
      return response;
    }

    if (response?.data?.code === 401) {
      if (response?.data?.data.isRedirect !== false) {
        toasterMsg(response.data.message, 'danger');
        setTimeout(() => {
          window.location.assign(`${window.location.origin}/development-order-overview`);
        }, 1300);
      }
    }
    if (response?.data?.SESSION_TIMED_OUT) {
      toasterMsg('Your session has timed out. Please login to continue.', 'danger');
      localStorageService.remove('planningFilter');
      localStorageService.remove('loggedInUserInfo');
      localStorageService.remove('rememberMe');
      setTimeout(() => {
        window.location.assign(`${window.location.origin}/login`);
      }, 1500);
      return;
    }
    if (response?.data?.UNAUTHORIZED_ACCESS) {
      toasterMsg("You don't have permission to access this module", 'danger');
      localStorageService.remove('planningFilter');
      setTimeout(() => {
        window.location.assign(`${window.location.origin}/login`);
      }, 3000);
      return;
    }
    if (response?.data?.UNAUTHORIZED_USER_RIGHT_ACCESS) {
      toasterMsg("You don't have permission to update this module", 'danger');
      localStorageService.remove('planningFilter');
      setTimeout(() => {
        window.location.assign(`${window.location.origin}/development-order-overview`);
      }, 3000);
      return;
    }
    if (response?.data?.UNAUTHORIZED_MODULE_RIGHT_ACCESS) {
      toasterMsg("You don't have permission to update this module", 'danger');
      localStorageService.remove('planningFilter');
      return;
    }
    if (response?.data?.errMsg === 'UNAUTHORIZED') {
      toasterMsg("You don't have permission to update this module", 'danger');
      localStorageService.remove('planningFilter');
      setTimeout(() => {
        window.location.assign(`${window.location.origin}/react/login`);
      }, 3000);
      return;
    }
    if (response.request.responseType === 'arraybuffer' && response.data && typeof response.data.byteLength === 'number') {
      try {
        const text = new TextDecoder('utf-8').decode(new Uint8Array(response.data));
        const json = JSON.parse(text);
        if (json && typeof json === 'object') {
          return { ...response, data: json };
        }
      } catch (notJson) {
        return response;
      }
      return response;
    }
    return response.data;
  },
  (error) => {
    untrackNavCancelSource(error?.config);
    if (axios.isCancel(error)) {
      const cancelError = new Error('Request cancelled due to navigation');
      cancelError.isNavigationCancelled = true;
      cancelError.__CANCEL__ = true;
      return Promise.reject(cancelError);
    }
    if (!error?.response && !axios.isCancel(error)) {
      reportNetworkFailure();
    }
    const status = error?.response?.status;
    const contentType = error?.response?.headers?.['content-type'];

    if (status === 429) {
      const retryAfter = parseInt(error?.response?.headers?.['retry-after'], 10);
      const minutes = Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter / 60) : 5;
      toasterMsg(i18next.t('LOGIN.TOO_MANY_ATTEMPTS', { ns: 'common', minutes }), 'danger');
      return Promise.reject(error);
    }

    if (error?.config?.skipSessionExpiryHandling) {
      return Promise.reject(error);
    }

    if ((status === 401 && contentType?.includes('text/html')) || error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
      toasterMsg('Your session has timed out. Please login to continue.', 'danger');
      localStorageService.remove('loggedInUserInfo');
      localStorageService.remove('rememberMe');
      delete apiClient.defaults.headers.common.Authorization;

      setTimeout(() => {
        window.location.assign(`${window.location.origin}/login`);
      }, 1500);
      return;
    }

    if (status === 403 && error?.response?.data?.message === RIGHTS_DENIED_KEY) {
      redirectWithoutPermission();
    }

    return Promise.reject(error);
  },
);

const RIGHTS_DENIED_KEY = 'BACKEND_SERVER.USER_RIGHT_SERVICE.UNAUTHORISED_ACCESS';
const NO_PERMISSION_LANDING = '/development-order-overview';
const NO_PERMISSION_REDIRECT_DELAY_MS = 1500;
let noPermissionRedirectPending = false;

/**
 * Sends a user whose rights-matrix check was refused (403 with the shared unauthorised-access key)
 * back to the landing page with a translated toast. Runs once per page load, so parallel refused
 * requests do not stack toasts or redirects.
 * @returns {void}
 */
function redirectWithoutPermission() {
  if (noPermissionRedirectPending) {
    return;
  }
  noPermissionRedirectPending = true;
  toasterMsg(i18next.t(RIGHTS_DENIED_KEY, { ns: 'common' }), 'danger');
  setTimeout(() => {
    window.location.assign(`${window.location.origin}${NO_PERMISSION_LANDING}`);
  }, NO_PERMISSION_REDIRECT_DELAY_MS);
}
